import {
  ArrowPathIcon,
  Card,
  CurrencyDollarIcon,
  formatDate,
  formatNumber,
  FunnelIcon,
  GradientCard,
  Input,
  PrinterIcon,
  Table,
  useCharity,
  useSearch,
} from "@";

import {
  generalReportColumnsA,
  generalReportColumnsB,
} from "./tablesColumns.jsx";
import { useGeneralReportQuery } from "../utils/queries.js";

const GeneralReport = () => {
  // stats and hooks
  const { charityLogo, charityName } = useCharity();
  const { search, updateSearch } = useSearch({ fromDate: null, toDate: null });

  const { data, isLoading } = useGeneralReportQuery(search.values);

  const reset = () => {
    updateSearch({ values: { fromDate: null, toDate: null } });
  };

  return (
    <>
      {/* charity logo and name for printing */}
      <div className="mb-8 hidden print:flex print:items-center">
        <img
          src={charityLogo}
          alt="charityLogo"
          className="h-20 w-20 rounded-md bg-white object-cover"
        />

        <span className="ms-5 text-xl">{charityName}</span>
      </div>

      {/* header */}
      <div className="mb-3 flex items-center gap-5 print:hidden">
        <button
          type="button"
          data-dropdown-toggle="resendAll"
          data-dropdown-placement="bottom"
          className="btn-white"
        >
          <FunnelIcon className="h-5 w-5" />

          <span>فلترة النتائج</span>
        </button>

        <div
          id="resendAll"
          className="z-50 hidden w-64 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-md dark:border-gray-600 dark:bg-gray-700 sm:w-96 sm:p-3"
        >
          <div className="flex flex-col items-end justify-between gap-3 sm:flex-row">
            <Input
              star
              showTimeSelect
              type="date-range"
              labelA="من تاريخ"
              labelB="الي تاريخ"
              valueA={search.values.fromDate}
              valueB={search.values.toDate}
              changeA={(e) =>
                updateSearch({
                  values: { ...search.values, fromDate: e },
                })
              }
              changeB={(e) =>
                updateSearch({
                  values: { ...search.values, toDate: e },
                })
              }
            />
            <button onClick={reset} type="button" className="btn-purple-900">
              <ArrowPathIcon className="w-5" />
            </button>
          </div>
        </div>

        <button onClick={() => window.print()} className="btn-purple-900 w-fit">
          <PrinterIcon className="me-2 w-5" />
          طباعة
        </button>
      </div>

      <main className="donation-reports relative">
        {/* donations */}
        <div className="relative mb-5 grid gap-x-5 lg:grid-cols-3 print:grid print:grid-cols-1">
          <Card title="التبرعات" className="lg:col-span-2">
            <div className="grid gap-5 rounded-2xl bg-[#F9FAFB] p-3 text-lg font-semibold dark:bg-gray-800 dark:p-0 md:grid-cols-2 md:p-6 print:grid print:grid-cols-3">
              {/* Total donations amount */}
              <div className="rounded-2xl bg-purple-50 p-3 text-center text-purple-900 dark:bg-gray-700 dark:text-gray-400 md:col-span-2">
                <span className="inline-flex min-h-16 min-w-16 items-center justify-center rounded-full bg-purple-100 dark:bg-gray-800 dark:bg-opacity-60">
                  {formatNumber(data?.totalDonationsAmount)}
                </span>
                <p>اجمالي التبرعات</p>
              </div>

              {/* Total donations count */}
              <div className="rounded-2xl bg-[#EDFAFA] p-3 text-center text-[#047481] dark:bg-gray-700 dark:text-gray-400">
                <span className="inline-flex min-h-16 min-w-16 items-center justify-center rounded-full bg-[#D5F5F6] dark:bg-gray-800 dark:bg-opacity-60">
                  {formatNumber(data?.totalDonationsCount)}
                </span>
                <p>عمليات التبرع</p>
              </div>

              {/* donors count */}
              <div className="rounded-2xl bg-[#FFF8F1] p-3 text-center text-[#D03801] dark:bg-gray-700 dark:text-gray-400">
                <span className="inline-flex min-h-16 min-w-16 items-center justify-center rounded-full bg-[#FEECDC] dark:bg-gray-800 dark:bg-opacity-60">
                  {formatNumber(data?.donorsCount)}
                </span>
                <p>عدد المتبرعين</p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-4 print:grid print:grid-cols-3">
            {/* donation amount average */}
            <GradientCard
              prefix="ريال"
              title="متوسط عملية التبرع"
              number={formatNumber(data?.donationAmountAverage)}
            >
              <CurrencyDollarIcon className="h-9 w-9" />
            </GradientCard>

            {/* most requested day */}
            <GradientCard
              title="الأيام الأكثر طلباً"
              description={`${formatNumber(
                data?.mostRequestedDay?.donationsCount,
              )} طلبات`}
              number={formatDate(data?.mostRequestedDay?.day)}
            >
              <CurrencyDollarIcon className="h-9 w-9" />
            </GradientCard>

            {/* most requested hour */}
            <GradientCard
              title="الساعات الأكثر طلبا"
              description={`${formatNumber(
                data?.mostRequestedHour?.donationsCount,
              )} طلبات`}
              number={data?.mostRequestedHour?.time}
            >
              <CurrencyDollarIcon className="h-9 w-9" />
            </GradientCard>
          </div>
        </div>

        <div className="grid gap-x-5 xl:grid-cols-2 print:grid print:grid-cols-2">
          {/* most requested projects */}
          <Card title="المشروعات الأكثر تبرعاً">
            <Table
              isSmall
              localPg
              isLoading={isLoading}
              columns={generalReportColumnsA}
              data={
                data?.mostRequestedProjects ? [data?.mostRequestedProjects] : []
              }
            />
          </Card>

          {/* most requested donors */}
          <Card title=" المتبرعين الأكثر طلباً">
            <Table
              isSmall
              localPg
              isLoading={isLoading}
              columns={generalReportColumnsB}
              data={
                data?.mostRequestedDonors ? [data?.mostRequestedDonors] : []
              }
            />
          </Card>
        </div>
      </main>
    </>
  );
};

export default GeneralReport;
